import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { isFirebaseConfigured } from '@/config/env';
import { routePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';
import { FIRESTORE_ERROR_MESSAGES } from '@/shared/utils/firestoreError';
import { isUserApproved, type UserProfile } from '@/types/user';

function accessBlockedMessage(profile: UserProfile | null): string | null {
  if (!profile) return null;
  if (profile.role === 'pending') return FIRESTORE_ERROR_MESSAGES.pending;
  if (!profile.active) return FIRESTORE_ERROR_MESSAGES.inactive;
  return null;
}

export function ProtectedRoute() {
  const { isAuthenticated, loading, firebaseUser, profile, profileError } =
    useAuth();
  const location = useLocation();

  if (!isFirebaseConfigured()) {
    const isProd = import.meta.env.PROD;
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1>Configuración pendiente</h1>
          {isProd ? (
            <p>
              Firebase no está en el build de producción. Revisá las variables{' '}
              <code>VITE_FIREBASE_*</code> en Cloudflare Pages, corregí el deploy
              (Retry deployment) o verificá <code>.env.production</code> en el
              repo. Guía: <code>docs/PAGES_DEPLOY.md</code>.
            </p>
          ) : (
            <p>
              Firebase no está configurado. Creá <code>.env.local</code> con las
              variables <code>VITE_FIREBASE_*</code> (ver <code>.env.example</code>
              ).
            </p>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="auth-screen">
        <p className="auth-muted">Cargando sesión…</p>
      </div>
    );
  }

  if (!firebaseUser) {
    return (
      <Navigate
        to={routePaths.login}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (!profile || !isUserApproved(profile)) {
    return (
      <Navigate
        to={routePaths.accessPending}
        replace
        state={{
          message:
            profileError ??
            accessBlockedMessage(profile) ??
            FIRESTORE_ERROR_MESSAGES.pending,
          profile,
        }}
      />
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={routePaths.login} replace />;
  }

  return <Outlet />;
}
